import React from 'react';
import { ActiveCustomer } from '../../../types/organizations';

interface APArchetype {
  id: string;
  name: string;
  description: string;
}

interface APArchetypesAssignmentModalProps {
  isOpen: boolean;
  customer: ActiveCustomer | null;
  availableAPArchetypes: APArchetype[];
  selectedAPArchetypes: string[];
  assignmentLoading: boolean;
  assignmentError: string;
  onClose: () => void;
  onToggleArchetype: (archetypeId: string) => void;
  onSubmit: () => void;
}

const APArchetypesAssignmentModal: React.FC<APArchetypesAssignmentModalProps> = ({
  isOpen,
  customer,
  availableAPArchetypes,
  selectedAPArchetypes,
  assignmentLoading,
  assignmentError,
  onClose,
  onToggleArchetype,
  onSubmit,
}) => {
  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Assign AP Archetypes - {customer.name}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Available AP Archetypes */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Select AP Archetypes</h4>
            <div className="space-y-2 max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {availableAPArchetypes.length > 0 ? (
                availableAPArchetypes.map((archetype) => (
                  <label key={archetype.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedAPArchetypes.includes(archetype.id)}
                      onChange={() => onToggleArchetype(archetype.id)}
                      className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">{archetype.name}</div>
                      <div className="text-sm text-gray-600 mt-1">{archetype.description}</div>
                    </div>
                  </label>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  All available AP archetypes are already connected to this customer.
                </div>
              )}
            </div>
          </div>

          {assignmentError && (
            <div className="text-red-600 text-sm">{assignmentError}</div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={assignmentLoading || selectedAPArchetypes.length === 0}
              className={`px-4 py-2 rounded-md transition-colors ${
                assignmentLoading || selectedAPArchetypes.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {assignmentLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Assigning...
                </div>
              ) : (
                `Assign ${selectedAPArchetypes.length} Archetype${selectedAPArchetypes.length !== 1 ? 's' : ''}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APArchetypesAssignmentModal;
